import React, { useCallback, useEffect, useState } from 'react'
import DataGrid, { Column, RowDragging, Scrolling } from 'devextreme-react/data-grid'
import axios from 'axios'
import notify from '../../utils/notify'
import { LoadIndicator } from 'devextreme-react/load-indicator'

type PopupArgs = {
	folder?: string
	model: string
	key: string
	insert: string
	displayField: string
	displayFieldRight?: string
	displayCaption?: string
	titleLeft?: string
	titleRight?: string
	itemLabel?: string
	successMessage?: string
	warning?: string
	folderRight?: string
	modelRight?: string
}

type DataGridPopupProps = {
	args: PopupArgs
	filter: any
}

type DataGridPopupSideProps = {
	args: PopupArgs
	filter: any
	side: 'left' | 'right'
}

const getFolderFromUrl = (): string => {
	try {
		const params = new URLSearchParams(window.location.search)
		return params.get('folder') || ''
	} catch {
		return ''
	}
}

const DataGridPopupSide: React.FC<DataGridPopupSideProps> = ({ args, filter, side }) => {
	const {
		model,
		folder: folderArg,
		key,
		insert,
		displayField,
		displayCaption,
		displayFieldRight,
		titleLeft,
		titleRight,
		itemLabel = 'item',
		successMessage,
		folderRight,
		modelRight,
	} = args

	const [rows, setRows] = useState<any[]>([])
	const [loading, setLoading] = useState(false)
	const [initialLoading, setInitialLoading] = useState(true)
	const folder = folderArg || getFolderFromUrl()

	const isRightSide = side === 'right'

	const dataModel = isRightSide && modelRight ? modelRight : model
	const dataFolder = isRightSide && folderRight ? folderRight : folder

	const sideDisplayField = isRightSide && displayFieldRight ? displayFieldRight : displayField
	const otherDisplayField = isRightSide && displayFieldRight ? displayField : displayFieldRight || displayField

	const heading = (isRightSide ? titleRight : titleLeft) || 'Select item (Drag & Drop)'

	const loadRows = useCallback(async () => {
		setInitialLoading(true)
		try {
			const body: any = {
				skip: 0,
				take: 1000,
				model: dataModel,
			}
			if (dataFolder) {
				body.folder = dataFolder
			}
			const response = await axios.post(`${API_FIBER_URL}/view/data`, body)
			const data = response.data?.data || []
			setRows(Array.isArray(data) ? data : [])
		} catch (err) {
			console.error('Error loading popup data', err)
			notify('Error loading popup data', 'error')
		} finally {
			setInitialLoading(false)
		}
	}, [dataFolder, dataModel])

	useEffect(() => {
		loadRows()
	}, [loadRows])

	useEffect(() => {
		const handler = (e: any) => {
			const detail = e.detail || {}
			const id = detail.id
			const from = detail.from
			const to = detail.to
			const row = detail.row
			if (id == null) return

			setRows(prev => {
				if (from === side) {
					return prev.filter(r => r[key] !== id)
				}

				if (to === side) {
					const exists = prev.some(r => r[key] === id)
					if (exists) return prev

					let newRow = row

					if (sideDisplayField && otherDisplayField) {
						const hasSideField = row && Object.prototype.hasOwnProperty.call(row, sideDisplayField)
						const hasOtherField = row && Object.prototype.hasOwnProperty.call(row, otherDisplayField)
						if (!hasSideField && hasOtherField) {
							newRow = {
								...row,
								[sideDisplayField]: row[otherDisplayField],
							}
						}
					}

					return [...prev, newRow]
				}
				return prev
			})
		}

		window.addEventListener('dg-popup-move', handler as EventListener)
		return () => window.removeEventListener('dg-popup-move', handler as EventListener)
	}, [side, key, sideDisplayField, otherDisplayField])

	const onAdd = useCallback(
		(e: any) => {
			e.cancel = true
			const itemId = e.itemData[key]
			if (itemId == null) {
				notify(`Cannot determine ${itemLabel} id.`, 'error')
				return
			}

			const active = side === 'right' ? 1 : 0

			setLoading(true)
			const payload = {
				filter,
				[key]: itemId,
				model,
				folder,
				insert,
				active,
			}

			axios
				.post(`${API_FIBER_URL}/popup/insert`, payload)
				.then(() => {
					setLoading(false)
					const from = e.fromData
					const to = e.toData

					window.dispatchEvent(
						new CustomEvent('dg-popup-move', {
							detail: {
								id: itemId,
								from,
								to,
								row: e.itemData,
								active,
							},
						})
					)

					notify(successMessage || `Selected ${itemLabel} has been updated successfully.`, 'success')
				})
				.catch(error => {
					setLoading(false)
					const errorMsg =
						error.response?.data?.message || error.response?.data?.error || error.message || 'Unknown error'
					console.error('Update error:', error.response?.data || error)
					notify(`Error applying changes: ${errorMsg}`, 'error')
				})
		},
		[filter, model, folder, insert, itemLabel, successMessage, key, side]
	)

	return (
		<>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: 10,
				}}
			>
				<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
					<p style={{ margin: 0, fontWeight: 'bold', fontSize: 20 }}>{heading}</p>
					{initialLoading && (
						<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
							<span style={{ fontSize: 12, color: '#666' }}>Loading...</span>
							<LoadIndicator height={20} width={20} />
						</div>
					)}
				</div>
				{loading && (
					<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
						<span>Wait until {itemLabel}s updated...</span>
						<LoadIndicator height={20} width={20} />
					</div>
				)}
			</div>
			{!initialLoading && (
				<DataGrid
					keyExpr={key}
					dataSource={rows}
					height={'54vh'}
					showBorders={true}
					columnAutoWidth={true}
					noDataText={isRightSide ? 'Please select items to start processing.' : 'No data'}
					loadPanel={{ enabled: false }}
					rowAlternationEnabled={true}
					focusedRowEnabled={false}
				>
					<RowDragging
						data={side}
						group="dataGridPopupGroup"
						onAdd={onAdd}
						allowReordering={false}
						dragDirection="both"
						showDragIcons={true}
					/>
					<Scrolling mode="virtual" />
					<Column
						key={sideDisplayField}
						dataField={sideDisplayField}
						caption={displayCaption || sideDisplayField}
					/>
				</DataGrid>
			)}
		</>
	)
}

const warningStyle: React.CSSProperties = {
	margin: '0 0 16px 0',
	color: '#b26a00',
	backgroundColor: '#fff3cd',
	border: '1px solid #ffeeba',
	padding: '8px 12px',
	fontSize: 13,
	borderRadius: 4,
	textAlign: 'center',
}

const DataGridPopup: React.FC<DataGridPopupProps> = ({ args, filter }) => {
	const { warning } = args

	return (
		<div className="widget-container">
			<style>{`
				.dx-datagrid-rowsview .dx-row { cursor: move !important; }
				.dx-datagrid-rowsview .dx-row:hover { background-color: #f5f5f5 !important; }
				.dx-datagrid-rowsview .dx-row.dx-row-focused { border: none !important; outline: none !important; }
				.dx-drag-source { opacity: 0.5 !important; }
				.dx-command-drag { pointer-events: auto !important; cursor: grab !important; }
				.dx-command-drag:active { cursor: grabbing !important; }
				.dx-datagrid-rowsview .dx-row * { pointer-events: auto !important; }
			`}</style>

			{warning && <p style={warningStyle}>{warning}</p>}

			<div style={{ display: 'flex', gap: 24 }}>
				<div style={{ flex: 1 }}>
					<DataGridPopupSide args={args} filter={filter} side="left" />
				</div>
				<div style={{ flex: 1 }}>
					<DataGridPopupSide args={args} filter={filter} side="right" />
				</div>
			</div>
		</div>
	)
}

export default DataGridPopup
